import {
  Paper,
  Toolbar,
  Typography,
  IconButton,
  TextField,
  Chip,
  useMediaQuery,
  Theme,
  Button,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CancelIcon from '@material-ui/icons/Cancel';
import EditIcon from '@material-ui/icons/Edit';
import TodayIcon from '@material-ui/icons/Today';
import * as React from 'react';
import { DelayedLinearProgress, SortableTable } from 'components';
import { SortableTableHeader } from 'components/SortableTable/components';
import { ReservationService } from 'services';
import { Reservation } from 'interfaces';
import { EditReservationDialog } from '../EditReservationDialog';
import { CancelReservationDialog } from '../CancelReservationDialog';

interface Props {
  refreshKey: number;
}

const columns: SortableTableHeader[] = [
  { key: 'time', label: 'Time' },
  { key: 'name', label: 'Name' },
  { key: 'party_size', label: 'Party Size' },
  { key: 'outlet', label: 'Outlet' },
  { key: 'table_names', label: 'Table' },
  { key: 'status', label: 'Status' },
  { key: 'experience', label: 'Experience' },
  { key: 'phone', label: 'Phone' },
  { key: 'source', label: 'Source' },
  { key: 'controls', sortable: false },
];

const statusColor = (status: string) => {
  switch (status) {
    case 'approved':
      return '#43a047';
    case 'completed':
      return '#1e88e5';
    case 'canceled':
      return '#9e9e9e';
    case 'declined':
      return '#e53935';
    case 'no_show':
      return '#fb8c00';
    default:
      return '#757575';
  }
};

const formatDateInput = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

export const DailyTab: React.FC<Props> = ({ refreshKey }) => {
  const [loading, setLoading] = React.useState(false);
  const [date, setDate] = React.useState<string>(formatDateInput(new Date()));
  const [search, setSearch] = React.useState('');
  const [rows, setRows] = React.useState<Reservation[]>([]);
  const [editing, setEditing] = React.useState<Reservation | null>(null);
  const [canceling, setCanceling] = React.useState<Reservation | null>(null);
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));

  const fetchRows = React.useCallback(() => {
    setLoading(true);
    ReservationService.getAll({
      date,
      search: search.trim() || undefined,
    })
      .then((data) => {
        const filtered = (data || []).filter(
          (r) => r.status !== 'requested' && r.status !== 'declined',
        );
        setRows(filtered);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [date, search]);

  React.useEffect(() => {
    fetchRows();
  }, [fetchRows, refreshKey]);

  const tableRows = rows.map((r) => {
    const dt = new Date(r.reservedAt);
    return {
      key: String(r.id),
      cells: [
        {
          key: 'time',
          display: dt.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
        },
        { key: 'name', display: r.name },
        { key: 'party_size', display: r.partySize },
        { key: 'outlet', display: r.outletName || '—' },
        {
          key: 'table_names',
          display:
            r.tableNames && r.tableNames !== 'null' ? r.tableNames : '—',
        },
        {
          key: 'status',
          display: (
            <Chip
              size="small"
              label={r.status}
              style={{ background: statusColor(r.status), color: 'white' }}
            />
          ),
        },
        { key: 'experience', display: r.experience?.title || '—' },
        { key: 'phone', display: r.phoneNumber },
        { key: 'source', display: r.source },
        {
          key: 'controls',
          display: (
            <div style={{ display: 'flex', gap: 2 }}>
              {r.status !== 'canceled' && r.status !== 'completed' && (
                <IconButton
                  size="small"
                  title="Cancel reservation"
                  onClick={() => setCanceling(r)}
                >
                  <CancelIcon fontSize="small" style={{ color: '#e53935' }} />
                </IconButton>
              )}
              <IconButton
                size="small"
                title="View / edit"
                onClick={() => setEditing(r)}
              >
                <ChevronRightIcon />
              </IconButton>
            </div>
          ),
        },
      ],
    };
  });

  const navigate = (deltaDays: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + deltaDays);
    setDate(formatDateInput(d));
  };

  return (
    <React.Fragment>
      <Paper style={{ padding: 8, marginBottom: 8 }}>
        <Toolbar style={{ gap: 8, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate(-1)}>
            <ChevronLeftIcon />
          </IconButton>
          <TextField
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <IconButton onClick={() => navigate(1)}>
            <ChevronRightIcon />
          </IconButton>
          <IconButton onClick={() => setDate(formatDateInput(new Date()))}>
            <TodayIcon />
          </IconButton>
          <TextField
            placeholder="Search name / email / phone / table"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            style={{ flex: 1, minWidth: 240 }}
          />
          <Typography variant="caption" color="textSecondary">
            {rows.length} bookings
          </Typography>
        </Toolbar>
      </Paper>

      {loading && <DelayedLinearProgress />}

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.length === 0 && !loading && (
            <Paper style={{ padding: 24, textAlign: 'center', color: '#888' }}>
              No bookings on this date.
            </Paper>
          )}
          {rows.map((r) => {
            const dt = new Date(r.reservedAt);
            const time = isNaN(dt.getTime())
              ? '—'
              : dt.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                });
            const cancelable =
              r.status !== 'canceled' && r.status !== 'completed';
            return (
              <Paper
                key={r.id}
                style={{
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
                onClick={() => setEditing(r)}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {time}
                  </span>
                  <Chip
                    size="small"
                    label={r.status}
                    style={{
                      background: statusColor(r.status),
                      color: 'white',
                      textTransform: 'capitalize',
                    }}
                  />
                </div>
                <div
                  style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2 }}
                >
                  {r.name}
                  <span
                    style={{
                      fontWeight: 400,
                      marginLeft: 8,
                      color: '#666',
                    }}
                  >
                    · party {r.partySize}
                    {r.outletName ? ' · ' + r.outletName : ''}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 14,
                    flexWrap: 'wrap',
                    fontSize: 13,
                    color: '#555',
                  }}
                >
                  {r.tableNames && r.tableNames !== 'null' && (
                    <span>
                      <strong>Table:</strong> {r.tableNames}
                    </span>
                  )}
                  {r.phoneNumber && (
                    <span>
                      <strong>Phone:</strong> {r.phoneNumber}
                    </span>
                  )}
                  <span>
                    <strong>Source:</strong> {r.source}
                  </span>
                </div>
                {r.experience && (
                  <div style={{ fontSize: 13, color: '#1D8B6E' }}>
                    Experience: <strong>{r.experience.title}</strong>
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    marginTop: 4,
                    flexWrap: 'wrap',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditing(r)}
                    style={{ flex: 1 }}
                  >
                    Edit
                  </Button>
                  {cancelable && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<CancelIcon />}
                      onClick={() => setCanceling(r)}
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </Paper>
            );
          })}
        </div>
      ) : (
        <Paper style={{ overflow: 'auto' }}>
          <SortableTable
            columns={columns}
            rows={tableRows}
            emptyTableText="No bookings on this date."
            disablePagination
          />
        </Paper>
      )}

      <EditReservationDialog
        open={!!editing}
        reservation={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          fetchRows();
        }}
      />

      <CancelReservationDialog
        open={!!canceling}
        reservation={canceling}
        onClose={() => setCanceling(null)}
        onCanceled={() => {
          setCanceling(null);
          fetchRows();
        }}
      />
    </React.Fragment>
  );
};
